import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { ApiService } from '@api/api.service';
import { User, UserCreate } from '@api/types';

import { SelectOption } from '@forms/select/select.component';

import { ModalService } from '@shared/modal/modal.service';
import { EncryptionHelperService } from '@shared/services/encryption-helper.service';

import { LocalStorageService } from '@shared/services/local-storage.service';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { StatusCodes } from '@shared/types/status-codes';

import { Subscription } from 'rxjs';

import { RoleService, UserRoleGroup } from 'src/app/security/role.service';

import { PhoneNumberValidator } from '../../validator/phone-number.validator';

@Component({
  selector: 'rav-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss']
})
export class CreateUserComponent implements OnInit, OnDestroy {
  @Input() accountId?: string;
  @Input() isAdmin?: boolean = false;
  @Input() userRoleGroup: [UserRoleGroup];

  isPlanCtaVisible = false;
  formGroup: FormGroup;
  displayRole = true;
  selectOptions: Array<SelectOption> = [];
  subscriptionList: Subscription = new Subscription();
  selectedUser: User;
  actionType: string;
  isCurrentUser: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private roleService: RoleService,
    private localStorage: LocalStorageService,
    private changeDetectorRef: ChangeDetectorRef,
    public modal: ModalService,
    private encryptionHelper: EncryptionHelperService
  ) {}

  ngOnInit() {
    this.getUserRoles();
    // Build form group only once
    this.buildFormGroup();

    this.subscriptionList.add(
      this.modal.open.subscribe((data) => {
        this.actionType = 'Create New';
        this.selectedUser = null;
        // Add / Remove password field
        this.togglePasswordField(data.modalId === 'newUser' && data.selectedUser);
        // Check if the user is being updated
        if (data.modalId === 'newUser' && data.selectedUser) {
          this.selectedUser = data.selectedUser;
          this.actionType = 'Update';

          const currentUserId = this.localStorage.userWithToken.id;
          this.isCurrentUser = currentUserId === this.selectedUser.id;
          // Patch user data because we're updating it
          this.loadUserToForm();
        }
      })
    );
    this.subscriptionList.add(
      this.modal.close.subscribe(() => {
        this.formGroup.reset();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptionList.unsubscribe();
  }

  /**
   * Get the roles for which the new user can be created.
   */
  getUserRoles(): void {
    if (this.userRoleGroup.includes(UserRoleGroup.Admin)) {
      this.selectOptions.push({ name: 'Super Admin', value: 'superadmin' });
      this.selectOptions.push({ name: 'Admin', value: 'admin' });
    }
    if (this.userRoleGroup.includes(UserRoleGroup.Main)) {
      this.selectOptions.push(
        { name: 'Read', value: 'read' },
        { name: 'View', value: 'view' },
        { name: 'Owner', value: 'owner' },
        { name: 'Editor', value: 'editor' },
        { name: 'Contributor', value: 'contributor' }
      );
    }
  }

  /**
   * Method used to add/remove password field
   *
   * @param removeFields Param used to decide if we should add or remove the password fields
   */
  togglePasswordField(removeFields: boolean = true) {
    if (removeFields) {
      this.formGroup.removeControl('pass');
      this.formGroup.removeControl('pass_conf');
    } else {
      this.formGroup.addControl('pass', new FormControl('', Validators.required));
      this.formGroup.addControl('pass_conf', new FormControl('', Validators.required));
    }
  }

  /**
   * Patch user values when we're updating the user
   */
  loadUserToForm(): void {
    this.formGroup.patchValue(this.selectedUser);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Builds the form group by creating all the form controls for every required field.
   */
  buildFormGroup() {
    if (this.selectedUser) {
      this.formGroup = this.formBuilder.group(
        {
          first_name: new FormControl(this.selectedUser.first_name, Validators.required),
          last_name: new FormControl(this.selectedUser.last_name, Validators.required),
          email: new FormControl(this.selectedUser.email, Validators.required),
          phone_number: new FormControl(''),
          role: new FormControl(this.selectedUser.role, Validators.required) // Password fields are added dynamically
          // See `subscriptionList` subscription on `ngOnInit`
          // When the user is new, add the passwords fields
          // If it is an update, the passwords fields are removed
        },
        { validators: PhoneNumberValidator.Validate }
      );

      return;
    }
    this.formGroup = this.formBuilder.group(
      {
        first_name: new FormControl('', Validators.required),
        last_name: new FormControl('', Validators.required),
        email: new FormControl('', Validators.required),
        phone_number: new FormControl('', Validators.required),
        role: new FormControl('', Validators.required),
        account_id: new FormControl('', Validators.required),
        pass: new FormControl('4acAgR2u4kxevRv5', Validators.required),
        pass_conf: new FormControl('4acAgR2u4kxevRv5', Validators.required) // Password fields are added dynamically
        // See `subscriptionList` subscription on `ngOnInit`
        // When the user is new, add the passwords fields
        // If it is an update, the passwords fields are removed
      },
      { validators: PhoneNumberValidator.Validate }
    );

    this.displayRole = true;
    this.subscriptionList.add(
      this.formGroup.get('role').valueChanges.subscribe((data) => {
        this.onRoleChange(this.roleService.getRoleGroup(data));
      })
    );
  }

  /**
   * When a role is selected, this function is triggered. First we check to see if
   * the selected role is an admin role. If so, the account_id must be 'system' and cannot
   * be overridden by the user. Otherwise if the role selected is not an admin, the user
   * is allowed to input the account id or name.
   *
   * @param role role that is selected by the role select element
   */
  onRoleChange(roleGroup: UserRoleGroup) {
    this.displayRole =
      roleGroup === null ||
      roleGroup === undefined ||
      roleGroup === UserRoleGroup.Admin ||
      (this.accountId !== null && this.accountId !== undefined);
    if (roleGroup === UserRoleGroup.Admin) this.formGroup.get('account_id').setValue('system');
    else if (this.accountId) {
      this.formGroup.get('account_id').setValue(this.accountId);
    } else this.formGroup.get('account_id').setValue('');
  }

  /**
   * Creates a new user
   *
   * A new user is created for the user with a temporary password and a reset email
   * is immediately sent and the modal is closed.
   *
   * @returns the email of the newly created user.
   */
  async createUser(newUser: UserCreate): Promise<string> {
    if (this.selectedUser) {
      this.updateUser(newUser);
      return;
    }

    try {
      const userEmail: string = await this.api.users
        .post(newUser)
        .toPromise()
        .then((user: User) => {
          if (this.encryptionHelper.isEncryptionEnabled()) {
            this.encryptionHelper.generateNewUserEncryptionKeys(user.id);
          }
          return user.email;
        });

      this.formGroup.reset();
      this.modal.close.next(userEmail);
      return userEmail;
    } catch (error) {
      if (
        error.error.errors.account_id &&
        error.error.errors.account_id[0] === 'A basic account may only have one user'
      ) {
        this.isPlanCtaVisible = true;
        return;
      }
      this.api.onFormError(error, this.formGroup);
    }
  }

  /**
   * Makes a request to update the user details.
   *
   * @param updatedUser new details for the user.
   */
  updateUser(updatedUser: any): void {
    for (const field in this.selectedUser) {
      if (this.selectedUser[field] === updatedUser[field]) {
        delete updatedUser[field];
      }
    }
    if (this.isCurrentUser) delete updatedUser.role;

    this.api.users.byId.patch(this.selectedUser.id, updatedUser).subscribe(
      (user: User) => {
        this.modal.close.next(user.email);
      },
      (error) => {
        this.api.onFormError(error, this.formGroup);
        if (error.status === StatusCodes.FORBIDDEN && (updatedUser.email || updatedUser.phone_number)) {
          this.modal.open.next({
            modalId: 'authentication',
            request: this.api.users.byId.patch(this.selectedUser.id, {
              email: updatedUser.email,
              phone_number: updatedUser.phone_number
            }),
            scope: 'super-user-write',
            type: 'profile'
          });
        }
      }
    );
  }
}
